import { type IdNameObject } from '@m/types';
import { apiFetchV2 } from '@m/utils/http';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { APPROACH_SETTING_IGNORE } from 'apps/admin/constants/Camera';

import { getLaneDetailQueryKey } from './query-keys';
import { adminLocationEquipment, adminLocationEquipmentById } from './routes';

const defaultCameraConfig = {
  topBound: 0,
  leftBound: 0,
  rightBound: 1,
  bottomBound: 1,
  minIntersection: 0,
  approachSettingType: APPROACH_SETTING_IGNORE,
};

export type AddLaneCameraPayload = {
  active: boolean;
  approachSettingType?: IdNameObject;
  directedLaneId: number;
  linkedSiteId?: number | null;
  model?: string | null;
  notes: string | null;
  serial: string | null;
  siteEquipmentType?: IdNameObject;
};

export type UpdateLaneCameraPayload = {
  active?: boolean;
  approachSettingType?: IdNameObject;
  directedLaneId: number;
  enabled?: boolean;
  id: number;
  linkedSiteId?: number | null;
  make?: string;
  model?: string | null;
  notes?: string | null;
  serial: string | null;
  siteEquipmentType?: IdNameObject;
};

type LaneCameraMutationVariables = {
  body: AddLaneCameraPayload;
  laneId: number;
  locationId: number;
};

type UpdateLaneCameraMutationVariables = {
  body: UpdateLaneCameraPayload;
  laneId: number;
  locationId: number;
};

type RemoveLaneCameraMutationVariables = {
  cameraId: number;
  laneId: number;
  locationId: number;
};

const addLaneCamera = ({
  locationId,
  laneId,
  body: { approachSettingType, ...body },
}: LaneCameraMutationVariables) =>
  apiFetchV2(adminLocationEquipment(locationId), {
    method: 'POST',
    body: {
      ...body,
      laneId,
      config: {
        ...defaultCameraConfig,
        approachSettingType,
      },
    },
  });

const updateLaneCamera = ({
  locationId,
  laneId,
  body: { approachSettingType, ...body },
}: UpdateLaneCameraMutationVariables) =>
  apiFetchV2(adminLocationEquipmentById(locationId, body.id), {
    method: 'PUT',
    body: {
      ...body,
      laneId,
      approachSettingType,
      config: {
        ...defaultCameraConfig,
        approachSettingType,
      },
    },
  });

const removeLaneCamera = ({ locationId, cameraId }: RemoveLaneCameraMutationVariables) =>
  apiFetchV2(adminLocationEquipmentById(locationId, cameraId), { method: 'DELETE' });

export const useAddLaneCamera = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addLaneCamera,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.locationId, variables.laneId),
      });
    },
  });
};

export const useUpdateLaneCamera = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLaneCamera,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.locationId, variables.laneId),
      });
    },
  });
};

export const useRemoveLaneCamera = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeLaneCamera,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.locationId, variables.laneId),
      });
    },
  });
};
